(function (host, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (host.document && !host.rafinedInternationalShippingProgress) {
    host.rafinedInternationalShippingProgress = api.mount(host);
  }
})(typeof window === 'object' ? window : globalThis, function () {
  'use strict';
  const BASE = 'kaching-cart__tiered-promotions-bar';
  // These are verified merchant settings, not a guessed currency exchange rate.
  // Revalidate this guard if automatic FX, payout currency or the conversion fee changes.
  const CONFIG = Object.freeze({ shopCurrency: 'USD', payoutCurrency: 'USD', automaticFx: true, conversionFee: 0.015 });
  const validMoney = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  const clamp = value => Math.max(0, Math.min(1, value));
  function currencyQuantum(currency) {
    try { return 10 ** (2 - new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits); }
    catch (_) { return null; }
  }
  function shippingThreshold(shopCents, rate, currency) {
    const quantum = currencyQuantum(currency);
    if (!quantum || !(rate > 0)) return null;
    // Shopify compares its rounded USD subtotal to the USD rate condition.
    // Therefore GBP52.90 can qualify even when 70 * rawRate is GBP52.90082.
    return Math.ceil(((shopCents - 0.5) * rate) / quantum - 1e-9) * quantum;
  }
  function selectedTiers(props, country) {
    const bar = props?.cartSettings?.tieredPromotionsBar;
    if (bar?.thresholdType !== 'cart-total') return null;
    const entries = Array.isArray(bar.marketSpecificTiers) ? bar.marketSpecificTiers : [];
    let entry = entries.find(value => value.countryCodes?.includes(country));
    if (!entry) entry = entries.find(value => value.countryCodes?.includes('*'));
    if (!entry && props.marketId) entry = entries.find(value => String(value.marketId).split('/').pop() === String(props.marketId).split('/').pop());
    if (!entry || entry.hidePromoBar || entry.useMarketCurrency || !Array.isArray(entry.tiers)) return null;
    const tiers = [...entry.tiers].sort((a, b) => a.threshold - b.threshold);
    if (tiers.length !== 3) return null;
    const gift = tiers.find(tier => tier.type === 'product-discount' && Number(tier.threshold) === 60);
    const shipping = tiers.find(tier => tier.type === 'shipping-discount' && Number(tier.threshold) === 70 && Number(tier.discountAmount) === 100);
    const order = tiers.find(tier => tier.type === 'order-discount' && Number(tier.threshold) === 100 && Number(tier.discountAmount) === 15);
    return gift && shipping && order ? { gift, shipping, order } : null;
  }
  function rewardTotal(cart, publicRate) {
    if (validMoney(cart?.totalAfterDiscounts)) return cart.totalAfterDiscounts;
    const state = cart?.cartPromotionsState;
    const next = state?.nextPromoToUnlock;
    if (validMoney(Number(next?.threshold)) && validMoney(state?.spendLeftForNextPromo)) {
      const target = Number((Number(next.threshold) * 100 * publicRate).toFixed(2));
      return Math.max(0, target - state.spendLeftForNextPromo);
    }
    return null;
  }
  function model(cart, props, shopify, config = CONFIG) {
    const country = String(shopify?.country || props?.countryCode || '').toUpperCase();
    if (!/^[A-Z]{2}$/.test(country) || country === 'US') return null;
    if (!config.automaticFx || config.shopCurrency !== 'USD' || config.payoutCurrency !== 'USD' || config.conversionFee !== 0.015) return null;
    const currency = cart?.currency;
    const publicRate = Number(shopify?.currency?.rate);
    if (!currency || shopify?.currency?.active !== currency || !(publicRate > 0) || !Number.isFinite(publicRate)) return null;
    if (props?.currencyCode && props.currencyCode !== currency) return null;
    if (Number(props?.currencyRate) > 0 && Math.abs(Number(props.currencyRate) - publicRate) > 1e-7) return null;
    const tiers = selectedTiers(props, country);
    const native = cart?.cartPromotionsState;
    if (!tiers || !Array.isArray(native?.unlockedPromos)) return null;
    const shippingTotal = validMoney(cart.total_price_without_shipping) ? cart.total_price_without_shipping :
      validMoney(cart.total_price) ? cart.total_price : null;
    if (shippingTotal === null) return null;
    const rawRate = currency === 'USD' ? 1 : publicRate / (1 + config.conversionFee);
    const threshold = shippingThreshold(Number(tiers.shipping.threshold) * 100, rawRate, currency);
    if (threshold === null) return null;
    const eligible = rewardTotal(cart, publicRate);
    const unlocked = Object.fromEntries(['gift', 'order'].map(role => [role,
      native.unlockedPromos.some(tier => tier.id === tiers[role].id)]));
    unlocked.shipping = Math.round(shippingTotal / rawRate) >= Number(tiers.shipping.threshold) * 100;
    // Gift/order amounts and actions keep Kaching's own eligible subtotal and
    // conversion; only shipping uses Shopify's shipping conversion basis.
    const nativeTarget = role => Number((Number(tiers[role].threshold) * 100 * publicRate).toFixed(2));
    const quantum = currencyQuantum(currency);
    const remaining = {
      gift: unlocked.gift ? 0 : eligible === null ? null : Math.max(0, Math.round((nativeTarget('gift') - eligible) / quantum) * quantum),
      shipping: unlocked.shipping ? 0 : Math.max(0, threshold - shippingTotal),
      order: unlocked.order ? 0 : eligible === null ? null : Math.max(0, Math.round((nativeTarget('order') - eligible) / quantum) * quantum)
    };
    if (remaining.gift === null || remaining.order === null) return { country, currency, tiers, unlocked, threshold, rawRate, shippingTotal, known: false };
    const ordered = ['gift', 'shipping', 'order'];
    const next = ordered.find(role => !unlocked[role]) || null;
    const latest = [...ordered].reverse().find(role => unlocked[role]) || null;
    let progress;
    if (!next) progress = 100;
    else if (next === 'gift') progress = clamp(eligible / nativeTarget('gift')) * 100 / 3;
    else if (next === 'shipping') progress = (1 + clamp((shippingTotal - nativeTarget('gift')) / (threshold - nativeTarget('gift')))) * 100 / 3;
    else progress = (2 + clamp((eligible - threshold) / (nativeTarget('order') - threshold))) * 100 / 3;
    return { country, currency, tiers, unlocked, threshold, rawRate, shippingTotal, eligible, remaining, next, latest, progress, known: true };
  }
  const label = role => ({ gift: 'Free gift', shipping: 'Free shipping', order: '15% off' })[role];
  function message(state, locale) {
    if (!state?.known) return null;
    const prefix = state.latest ? `${label(state.latest)} unlocked!` : '';
    if (!state.next) return prefix;
    if (state.remaining[state.next] === 0) return `${prefix} Updating your rewards…`.trim();
    const amount = new Intl.NumberFormat(locale || 'en', { style: 'currency', currency: state.currency }).format(state.remaining[state.next] / 100);
    return `${prefix} Add ${amount} to unlock ${label(state.next)}!`.trim();
  }
  function identifyIcons(bar, state) {
    const result = {};
    for (const role of ['gift', 'shipping', 'order']) {
      result[role] = [...bar.querySelectorAll(`.${BASE}-icon[data-tier-id]`)].find(icon => icon.getAttribute('data-tier-id') === state.tiers[role].id);
      if (!result[role]) return null;
    }
    return result;
  }
  function syncShippingIcon(host, root, icon, unlocked) {
    const styles = host.getComputedStyle(root);
    const primary = styles.getPropertyValue('--kaching-cart-brand-primary-accent').replace(/\s/g, '');
    const inactive = styles.getPropertyValue('--kaching-cart-tiered-promotions-bar-inactive').replace(/\s/g, '') || '152,162,179';
    if (!primary) return false;
    const color = 'rgb(' + (unlocked ? primary : inactive) + ')';
    const svg = [...icon.children].find(child => child.tagName.toLowerCase() === 'svg');
    const rects = svg?.querySelectorAll('rect');
    if (!rects || rects.length < 2) return false;
    // The global custom footer already reads this exact fill attribute. Keep its
    // existing logic compatible without changing app-wide custom HTML/JS.
    for (const [node, key] of [[rects[0], 'fill'], [rects[0], 'stroke'], [rects[1], 'fill']]) {
      if (node.getAttribute(key) !== color) node.setAttribute(key, color);
    }
    return true;
  }
  function mount(host) {
    const doc = host.document;
    let cart = null, pending = false, root = null, observer = null, initialObserver = null;
    const set = (node, key, value) => { if (node.getAttribute(key) !== String(value)) node.setAttribute(key, String(value)); };
    function reset(bar) {
      const icon = bar.querySelector('[data-rf-shipping-icon]');
      if (icon && Array.isArray(cart?.cartPromotionsState?.unlockedPromos)) {
        const nativeUnlocked = cart.cartPromotionsState.unlockedPromos.some(tier => tier.id === icon.getAttribute('data-tier-id'));
        syncShippingIcon(host, root, icon, nativeUnlocked);
      }
      bar.removeAttribute('data-rf-shipping-known');
      bar.removeAttribute('data-rf-shipping-unlocked');
      bar.style.removeProperty('--rf-shipping-progress');
      bar.querySelector('.rf-shipping-message')?.remove();
      for (const icon of bar.querySelectorAll('[data-rf-shipping-icon]')) icon.removeAttribute('data-rf-shipping-icon');
    }
    function render() {
      pending = false;
      const current = doc.getElementById('kaching-cart');
      if (!current) return;
      if (root !== current) {
        observer?.disconnect();
        root = current;
        observer = new host.MutationObserver(schedule);
        observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['fill', 'data-open'] });
      }
      initialObserver?.disconnect(); initialObserver = null;
      const cached = host.kachingCartApi?.getCartData?.();
      if (cached?.cartPromotionsState) cart = cached;
      const state = model(cart, host.__kachingCartProps, host.Shopify);
      let changed = false;
      for (const bar of root.querySelectorAll(`.${BASE}`)) {
        const icons = state?.known ? identifyIcons(bar, state) : null;
        if (!icons || !syncShippingIcon(host, root, icons.shipping, state.unlocked.shipping)) {
          if (bar.hasAttribute('data-rf-shipping-known')) { reset(bar); changed = true; }
          continue;
        }
        const signature = String(state.unlocked.shipping);
        if (bar.getAttribute('data-rf-shipping-known') !== 'true' || bar.getAttribute('data-rf-shipping-unlocked') !== signature) changed = true;
        set(bar, 'data-rf-shipping-known', 'true');
        set(bar, 'data-rf-shipping-unlocked', signature);
        set(icons.shipping, 'data-rf-shipping-icon', 'true');
        let note = bar.querySelector('.rf-shipping-message');
        if (!note) {
          note = doc.createElement('p');
          note.className = `${BASE}-text rf-shipping-message`;
          note.setAttribute('aria-live', 'polite'); note.setAttribute('aria-atomic', 'true');
          bar.prepend(note);
        }
        const text = message(state, doc.documentElement.lang);
        if (note.textContent !== text) note.textContent = text;
        const width = `${state.progress.toFixed(4)}%`;
        if (bar.style.getPropertyValue('--rf-shipping-progress') !== width) bar.style.setProperty('--rf-shipping-progress', width);
      }
      if (changed) doc.dispatchEvent(new host.CustomEvent('rafined:shipping-progress'));
    }
    function schedule() { if (!pending) { pending = true; host.requestAnimationFrame(render); } }
    function start() {
      if (!doc.getElementById('kaching-cart') && !initialObserver) {
        initialObserver = new host.MutationObserver(schedule);
        initialObserver.observe(doc.body, { childList: true, subtree: true });
      }
      schedule();
    }
    doc.addEventListener('kaching-cart:cart-updated', event => { cart = event.detail?.cart || null; schedule(); });
    doc.addEventListener('kaching-cart:open', schedule);
    doc.addEventListener('shopify:section:load', start);
    if (doc.body) start(); else doc.addEventListener('DOMContentLoaded', start, { once: true });
    return { active: true, refresh: schedule };
  }
  return { CONFIG, currencyQuantum, shippingThreshold, selectedTiers, rewardTotal, model, message, identifyIcons, syncShippingIcon, mount };
});
